import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import _get from 'lodash/get';
import { dateFormatForApi } from '@lib/enums/dateTime';
import MonitoringFilterFieldNames from '@lib/enums/fieldNames/monitoringFilterFieldNames';
import usePrevious from '@lib/hooks/usePrevious';
import { Option, Values } from '@lib/interfaces/form';
import { getFormattedDate, transformDate } from '@lib/utils/dateTimeHelpers';
import isAbortError from '@lib/utils/isAbortError';
import { APP_URLS } from 'constants/urls';
import dayjs, { Dayjs } from 'dayjs';
import {
  FilterGroupingEnum,
  SortDirectionEnum,
  Task,
  TaskScopeNameEnum,
} from 'graphql-common';
import { useWhoamiContext } from 'hooks/useWhoamiContext';
import { MonitoringEntity } from '../enums';
import getFilterFieldNameByMonitoringEntity from './getFilterFieldNameByMonitoringEntity';
import getFilterLastUpdate from './getFilterLastUpdate';
import getMonitoringEntity from './getMonitoringEntity';
import { UseQueryResult } from './useQueries';

export type UseActionsResult = {
  basePeriodEndParam: string;
  basePeriodStartParam: string;
  defaultMaxDateObj?: Dayjs;
  defaultMinDateObj?: Dayjs;
  isApplyPresetLoading: boolean;
  isLoading: boolean;
  isReady: boolean;
  onUpdateFilterParams: (values: Values) => void;
  onViewHandler: (task?: Task) => void;
  periodEndParam: string | null | undefined;
  periodStartParam: string | null | undefined;
  resetFilterParams: () => void;
  resetPeriodFilterParams: () => void;
};

function getDataFromFilterParams(
  filterParams: Values,
  monitoringEntity: MonitoringEntity,
  defaultMinDate: string,
  defaultMaxDate: string,
) {
  const basePeriod = filterParams[
    getFilterFieldNameByMonitoringEntity(
      monitoringEntity,
      MonitoringFilterFieldNames.Period,
    )
  ] as string[];
  const basePeriodStartParam = basePeriod?.[0] || defaultMinDate;
  const basePeriodEndParam = basePeriod?.[1] || defaultMaxDate;
  const periodStartParam = getFormattedDate(
    basePeriodStartParam,
    dateFormatForApi,
  );
  const periodEndParam = getFormattedDate(basePeriodEndParam, dateFormatForApi);

  return {
    basePeriodEndParam,
    basePeriodStartParam,
    periodEndParam,
    periodStartParam,
  };
}

export default function useActions({
  useQueriesResult,
  filterParams,
  setFilterParams,
}: {
  useQueriesResult: UseQueryResult;
  filterParams: Values;
  setFilterParams: (values: Values) => void;
}): UseActionsResult {
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [isApplyPresetLoading, setApplyPresetLoading] = useState(false);
  // A ref, not state: Strict Mode double-invokes the mount effect below with
  // no re-render in between, so a state flag would still let both invocations
  // through and fire the init queries twice.
  const isInitializedRef = useRef(false);
  const navigate = useNavigate();

  const monitoringEntity = getMonitoringEntity({ filterParams });
  const lastUpdate = getFilterLastUpdate({ filterParams });
  const prevLastUpdate = usePrevious(lastUpdate);

  const filterByPeriod = _get(
    filterParams,
    getFilterFieldNameByMonitoringEntity(
      monitoringEntity,
      MonitoringFilterFieldNames.Period,
    ),
    [],
  ) as string[];
  const filterBySites = _get(
    filterParams,
    MonitoringFilterFieldNames.Sites,
    [],
  ) as Option[];
  const filterBySitesIds = filterBySites.map((item) => item.value) as string[];
  const filterBySectors = _get(
    filterParams,
    MonitoringFilterFieldNames.Sectors,
    [],
  ) as Option[];
  const filterBySectorsIds = filterBySectors.map(
    (item) => item.value,
  ) as string[];

  const {
    fetchSectorsSearchListLazyQuery,
    fetchTasksSearchListLazyQuery,
    fetchSitesSearchListLazyQuery,
  } = useQueriesResult;

  // Whoami — reads ProtectedRoute's single query result. See useWhoamiContext.
  const { currentUser } = useWhoamiContext();

  // Data
  const defaultMinDate = _get(currentUser, 'company.createdAt') as string;
  const defaultMaxDate = useMemo(() => new Date().toISOString(), []);
  const defaultMinDateObj = defaultMinDate
    ? dayjs(defaultMinDate).startOf('day')
    : undefined;
  const defaultMaxDateObj = defaultMaxDate
    ? dayjs(defaultMaxDate).endOf('day')
    : undefined;
  const defaultMinDateStr = defaultMinDateObj
    ? transformDate(defaultMinDateObj.toDate())
    : undefined;
  const defaultMaxDateStr = defaultMaxDateObj
    ? transformDate(defaultMaxDateObj.toDate(), true)
    : undefined;
  const {
    basePeriodStartParam,
    basePeriodEndParam,
    periodStartParam,
    periodEndParam,
  } = getDataFromFilterParams(
    filterParams,
    monitoringEntity,
    defaultMinDate,
    defaultMaxDate,
  );

  // Actions
  const onViewHandler = (task?: Task) => {
    if (task) {
      const { id } = task;
      navigate(
        APP_URLS.dashboard.tasks.view.getDynamicPath({
          pathParts: { id },
        }),
      );
    }
  };

  const resetFilterParams = useCallback(() => {
    setFilterParams({
      ...filterParams,
      [getFilterFieldNameByMonitoringEntity(
        monitoringEntity,
        MonitoringFilterFieldNames.FormCategory,
      )]: null,
      [getFilterFieldNameByMonitoringEntity(
        monitoringEntity,
        MonitoringFilterFieldNames.FormTemplateName,
      )]: null,
    });
  }, [filterParams, monitoringEntity, setFilterParams]);

  const resetPeriodFilterParams = useCallback(() => {
    setFilterParams({
      ...filterParams,
      [getFilterFieldNameByMonitoringEntity(
        monitoringEntity,
        MonitoringFilterFieldNames.FilterPresetId,
      )]: undefined,
      [getFilterFieldNameByMonitoringEntity(
        monitoringEntity,
        MonitoringFilterFieldNames.Period,
      )]: [defaultMinDateStr, defaultMaxDateStr],
    });
  }, [
    setFilterParams,
    filterParams,
    monitoringEntity,
    defaultMinDateStr,
    defaultMaxDateStr,
  ]);

  const onUpdateFilterParams = useCallback(
    (values: Values) => {
      setFilterParams({ ...filterParams, ...values });
    },
    [filterParams, setFilterParams],
  );

  useEffect(() => {
    if (!filterByPeriod?.[0] || !filterByPeriod?.[1]) {
      onUpdateFilterParams({
        [getFilterFieldNameByMonitoringEntity(
          monitoringEntity,
          MonitoringFilterFieldNames.Period,
        )]: [defaultMinDateStr, defaultMaxDateStr],
      });
    }
  }, [
    defaultMaxDateStr,
    defaultMinDateStr,
    filterByPeriod,
    monitoringEntity,
    onUpdateFilterParams,
  ]);

  // Seeds the filters with the first sites/sectors once the user is known.
  // retain() is required: Apollo v4 rejects a superseded useLazyQuery execute
  // promise with an AbortError, and these query instances are shared with the
  // apply-preset effect below (and torn down/re-subscribed by Strict Mode), so
  // without it an unrelated call could abort this fetch and strand the page on
  // the empty state.
  useEffect(() => {
    if (isInitializedRef.current || !currentUser) {
      return;
    }
    isInitializedRef.current = true;
    Promise.all([
      fetchSitesSearchListLazyQuery({
        variables: {
          page: 1,
          limit: 6,
          sorts: { name: SortDirectionEnum.Asc },
          filters: filterBySitesIds?.length
            ? [
                {
                  id: {
                    grouping: FilterGroupingEnum.Or,
                    predicate: {
                      in: filterBySitesIds,
                    },
                  },
                },
              ]
            : undefined,
        },
      })
        .retain()
        .then((res) => res.data?.data),
      fetchSectorsSearchListLazyQuery({
        variables: {
          page: 1,
          limit: 6,
          sorts: { name: SortDirectionEnum.Asc },
          filters: filterBySectorsIds?.length
            ? [
                {
                  id: {
                    grouping: FilterGroupingEnum.Or,
                    predicate: {
                      in: filterBySectorsIds,
                    },
                  },
                },
              ]
            : undefined,
        },
      })
        .retain()
        .then((res) => res.data?.data),
      fetchTasksSearchListLazyQuery({
        variables: {
          page: 1,
          limit: 1,
          scope: { name: TaskScopeNameEnum.Completed },
        },
      })
        .retain()
        .then((res) => res.data?.data),
    ])
      .then((res) => {
        const [sites, sectors, tasks] = res;
        if (
          sites?.collection?.length &&
          sectors?.collection?.length &&
          tasks?.collection?.length
        ) {
          onUpdateFilterParams({
            [MonitoringFilterFieldNames.Sites]: sites.collection.map(
              (item) => ({ value: item.id, label: item.name }),
            ),
            [MonitoringFilterFieldNames.Sectors]: sectors.collection.map(
              (item) => ({
                value: item.id,
                label: item.name,
                data: item,
              }),
            ),
          });
          setIsReady(true);
        } else {
          setIsReady(false);
        }
        setLoading(false);
      })
      .catch((error) => {
        // With retain() above, an abort means the query was torn down for good
        // (e.g. unmount) - there is nothing left to render or report.
        if (isAbortError(error)) {
          return;
        }
        console.error('Error initializing queries:', error);
        setLoading(false);
      });
  }, [
    currentUser,
    fetchSectorsSearchListLazyQuery,
    fetchTasksSearchListLazyQuery,
    fetchSitesSearchListLazyQuery,
    onUpdateFilterParams,
    filterBySitesIds,
    filterBySectorsIds,
  ]);

  useEffect(() => {
    const shouldRefetchSites = filterBySites.some(({ label }) => !label);
    const shouldRefetchSectors = filterBySectors.some(({ label }) => !label);

    if (
      !prevLastUpdate ||
      lastUpdate === prevLastUpdate ||
      (!shouldRefetchSites && !shouldRefetchSectors)
    ) {
      return;
    }

    const requests: Array<
      Promise<{
        field: MonitoringFilterFieldNames;
        options: Option[];
      }>
    > = [];

    if (shouldRefetchSites) {
      requests.push(
        fetchSitesSearchListLazyQuery({
          variables: {
            page: 1,
            limit: 6,
            sorts: { name: SortDirectionEnum.Asc },
            filters: filterBySitesIds?.length
              ? [
                  {
                    id: {
                      grouping: FilterGroupingEnum.Or,
                      predicate: {
                        in: filterBySitesIds,
                      },
                    },
                  },
                ]
              : undefined,
          },
        }).then((res) => ({
          field: MonitoringFilterFieldNames.Sites,
          options:
            res.data?.data?.collection?.map((item) => ({
              value: item.id,
              label: item.name,
            })) || [],
        })),
      );
    }

    if (shouldRefetchSectors) {
      requests.push(
        fetchSectorsSearchListLazyQuery({
          variables: {
            page: 1,
            limit: 6,
            sorts: { name: SortDirectionEnum.Asc },
            filters: filterBySectorsIds?.length
              ? [
                  {
                    id: {
                      grouping: FilterGroupingEnum.Or,
                      predicate: {
                        in: filterBySectorsIds,
                      },
                    },
                  },
                ]
              : undefined,
          },
        }).then((res) => ({
          field: MonitoringFilterFieldNames.Sectors,
          options:
            res.data?.data?.collection?.map((item) => ({
              value: item.id,
              label: item.name,
              data: item,
            })) || [],
        })),
      );
    }

    if (!requests.length) {
      return;
    }

    setApplyPresetLoading(true);

    Promise.all(requests)
      .then((results) => {
        const nextFilters: Values = {};
        let hasSites = !shouldRefetchSites;
        let hasSectors = !shouldRefetchSectors;

        results.forEach(({ field, options }) => {
          if (field === MonitoringFilterFieldNames.Sites) {
            hasSites = options.length > 0;
          }

          if (field === MonitoringFilterFieldNames.Sectors) {
            hasSectors = options.length > 0;
          }

          if (options.length) {
            nextFilters[field] = options;
          }
        });

        if (hasSites && hasSectors) {
          if (Object.keys(nextFilters).length) {
            onUpdateFilterParams(nextFilters);
          }
          setIsReady(true);
        } else {
          setIsReady(false);
        }
      })
      .catch((error) => {
        console.error('Error initializing queries:', error);
        setIsReady(false);
      })
      .finally(() => {
        setApplyPresetLoading(false);
      });
  }, [
    filterParams,
    fetchSectorsSearchListLazyQuery,
    fetchSitesSearchListLazyQuery,
    filterBySectorsIds,
    filterBySitesIds,
    lastUpdate,
    onUpdateFilterParams,
    prevLastUpdate,
    filterBySites,
    filterBySectors,
  ]);

  return {
    basePeriodEndParam,
    basePeriodStartParam,
    defaultMaxDateObj,
    defaultMinDateObj,
    isApplyPresetLoading,
    isLoading,
    isReady,
    onUpdateFilterParams,
    onViewHandler,
    periodEndParam,
    periodStartParam,
    resetFilterParams,
    resetPeriodFilterParams,
  };
}
